'use client'

import { type PropsWithChildren, type RefObject, useRef } from 'react'
import { useResizeObserver } from 'usehooks-ts'

import RecommendColumnAdSlot from '@/app/[locale]/(worlds)/[roomId]/components/RecommendColumnAdSlot'
import { RECOMMEND_COLUMN_WIDTH, RECOMMEND_GRID_DESKTOP } from '@/constants/content-detail-layout'
import { useUAContext } from '@/context/UAContext'
import { cn } from '@/lib/utils'

type RecommendWorldsWrapperProps = PropsWithChildren<{
  desktopTitle?: string
}>

export default function RecommendWorldsWrapper({
  children,
  desktopTitle,
}: RecommendWorldsWrapperProps) {
  const { isMobile } = useUAContext()
  const recommendationGridRef = useRef<HTMLDivElement>(null)

  const { width: recommendationWidth = 0 } = useResizeObserver({
    ref: recommendationGridRef as RefObject<HTMLElement>,
    box: 'border-box',
  })

  return (
    <div
      data-section="recommend-worlds"
      data-nosnippet
      className={cn(
        'no-scrollbar flex flex-col gap-4 lg:col-start-2 lg:row-span-2 lg:pt-2 lg:pb-4 landscape:px-[94px]',
        !isMobile && RECOMMEND_COLUMN_WIDTH,
      )}
    >
      {!isMobile && (
        <RecommendColumnAdSlot type="desktop" recommendationWidth={recommendationWidth} />
      )}

      {!isMobile && desktopTitle && (
        <h2 className="text-vive-primary-0 w-full overflow-hidden text-[18px] leading-8 font-semibold tracking-[0.2px] text-ellipsis">
          {desktopTitle}
        </h2>
      )}

      <div
        ref={recommendationGridRef}
        className={cn('grid grid-cols-2 gap-4 max-md:px-4 md:grid-cols-4 landscape:grid-cols-4', {
          [RECOMMEND_GRID_DESKTOP]: !isMobile,
        })}
      >
        {children}
      </div>
    </div>
  )
}
